import { useState } from "react";
import { ChevronRight, FileText, Copy, Check } from "lucide-react";
import { formatEvidenceJson } from "../../utils/formatters";

/** Map an arbitrary status string to a tint bucket. */
function tint(status?: string): "passed" | "failed" | "pending" | "" {
  const v = (status ?? "").toLowerCase();
  if (v.includes("pass")) return "passed";
  if (v.includes("fail")) return "failed";
  if (v.includes("pending")) return "pending";
  return "";
}

function CopyAction({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        } catch {
          setCopied(false);
        }
      }}
      className="inline-flex items-center gap-1 text-[10px] font-bold text-[#64748b] dark:text-white/60 hover:text-indigo-600 dark:hover:text-violet-300 transition-colors"
    >
      {copied ? <Check className="size-3" /> : <Copy className="size-3" />}
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

const STATUS_LINE = /^(\s*"([^"]*)"\s*:\s*)("(?:[^"\\]|\\.)*")(,?)$/;

function statusTone(value: string): string | null {
  const v = value.trim().toLowerCase();
  if (v.includes("not satisfied") || v.includes("fail")) {
    return "text-red-600 dark:text-red-400";
  }
  if (v.includes("satisfied") || v.includes("pass")) {
    return "text-emerald-600 dark:text-emerald-400";
  }
  return null;
}

function JsonWithStatus({ text }: { text: string }) {
  const lines = text.split("\n");
  return (
    <>
      {lines.map((line, i) => {
        const nl = i < lines.length - 1 ? "\n" : "";
        const m = STATUS_LINE.exec(line);
        const key = m ? m[2].toLowerCase() : "";
        const tone = m && key.endsWith("status") ? statusTone(m[3].slice(1, -1)) : null;
        if (m && tone) {
          return (
            <span key={i}>
              {m[1]}
              <span className={`font-bold ${tone}`}>{m[3]}</span>
              {m[4]}
              {nl}
            </span>
          );
        }
        return (
          <span key={i}>
            {line}
            {nl}
          </span>
        );
      })}
    </>
  );
}

export function EvidenceJsonText({ content }: { content: string }) {
  const json = formatEvidenceJson(content);
  if (json === null) return <>{content}</>;
  return <JsonWithStatus text={json} />;
}

export function EvidenceRecords({ content }: { content: string }) {
  const json = formatEvidenceJson(content);
  const text = json ?? content;

  if (text.trim() === "") {
    return (
      <p className="px-1 py-2 text-[12px] italic text-[#94a3b8] dark:text-white/40">
        No evidence data.
      </p>
    );
  }

  return (
    <div className="rounded-lg border border-[#e2e8f0] dark:border-white/[0.08] overflow-hidden">
      <div className="flex items-center justify-between gap-2 px-3 py-1.5 bg-[#f8fafc] dark:bg-white/[0.04] border-b border-[#e2e8f0] dark:border-white/[0.08]">
        <span className="font-mono text-[11px] font-bold text-[#475569] dark:text-white/70">
          {json !== null ? "evidence.json" : "evidence.txt"}
        </span>
        <CopyAction text={text} />
      </div>
      <pre className="m-0 max-h-[70vh] overflow-auto bg-white dark:bg-white/[0.02] px-3 py-2.5 font-mono text-[12px] leading-relaxed whitespace-pre-wrap break-words text-[#0f172a] dark:text-white/90">
        {json !== null ? <JsonWithStatus text={text} /> : text}
      </pre>
    </div>
  );
}

/**
 * Inline polished code/evidence viewer — a collapsible code-editor style block:
 * header bar (file icon, label, copy), status-tinted border, raw content shown
 * verbatim (wrapped). Used for both the executed Command and the Evidence.
 * Each instance owns its open/copy state.
 */
export function CodeBlock({
  label,
  content,
  status,
  structured = false,
}: {
  label: string;
  content: string;
  status?: string;
  structured?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const st = tint(status);
  const passed = st === "passed";
  const pending = st === "pending";
  const barLabel = label.split(" ")[0].toLowerCase();
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable — ignore */
    }
  };
  const border = passed
    ? "border-emerald-200 dark:border-emerald-500/25"
    : pending
      ? "border-amber-200 dark:border-amber-500/25"
      : "border-red-200 dark:border-red-500/25";
  const headerBg = passed
    ? "bg-emerald-50/70 dark:bg-emerald-500/[0.08]"
    : pending
      ? "bg-amber-50/70 dark:bg-amber-500/[0.08]"
      : "bg-red-50/70 dark:bg-red-500/[0.08]";
  return (
    <div>
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 text-[11px] font-bold tracking-wide text-[#475569] dark:text-white/70 hover:text-indigo-600 dark:hover:text-violet-300 transition-colors"
      >
        <ChevronRight className={`size-3.5 transition-transform ${open ? "rotate-90" : ""}`} />
        {label}
      </button>
      {open && (
        <div className={`mt-1.5 rounded-lg border overflow-hidden ${border}`}>
          {/* Code header bar */}
          <div className={`flex items-center gap-2 px-3 py-1.5 border-b ${border} ${headerBg}`}>
            <FileText className="size-3.5 text-[#475569] dark:text-white/70 flex-shrink-0" />
            <span className="text-[11px] font-mono font-bold text-[#334155] dark:text-white/80">{barLabel}</span>
            <span className="flex-1" />
            <button
              onClick={copy}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold text-white bg-indigo-600 dark:bg-violet-600 hover:opacity-90 transition-opacity"
            >
              {copied ? <Check className="size-3" /> : <Copy className="size-3" />}
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
          {structured ? (
            <div className="max-h-96 overflow-auto bg-[#f8fafc] dark:bg-[#0f111a] p-2.5">
              <EvidenceRecords content={content} />
            </div>
          ) : (
            <div className="max-h-72 overflow-auto bg-[#f8fafc] dark:bg-[#0f111a]">
              <pre className="m-0 px-3 py-2 font-mono text-[11px] leading-relaxed whitespace-pre-wrap break-words text-[#334155] dark:text-white/85">{content}</pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
